import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import type { ITilesService } from '@/plugins/tiles/types'
import type { EngineContext } from '@/runtime/EngineContext'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { AssetHandle, AssetId, AssetKind } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  CadService,
  GaussianSplatService,
  PointCloudService,
  RenderContributor,
  RenderPipeline,
  ViewRegistry,
} from '@/shared/types/services'
import type { EngineFrameContext, ViewFrameContext } from '@/shared/types/view'
import { SpatialQueryServiceImpl } from './SpatialQueryService'

/**
 * 空间查询插件，提供 SpatialQueryService 并负责调试几何的 Overlay3D 渲染。
 */
export class SpatialQueryPlugin implements Plugin, RenderContributor {
  /** 插件名称 */
  readonly name = 'spatial-query'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 渲染贡献者类型标识 */
  readonly kind = 'spatial-query'
  /** 插件启动优先级 */
  readonly priority = 26
  /** 依赖的服务名列表 */
  readonly dependencies = ['ViewRegistry', 'RenderPipeline'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['SpatialQueryService'] as const
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  private context: EngineContext | null = null
  private viewRegistry: ViewRegistry | null = null
  private pointCloudService: PointCloudService | null = null
  private gaussianSplatService: GaussianSplatService | null = null
  private cadService: CadService | null = null
  private tilesService: ITilesService | null = null
  private service: SpatialQueryServiceImpl | null = null
  private renderPipeline: RenderPipeline | null = null
  private readonly debugScene = new THREE.Scene()
  private readonly debugRoot = new THREE.Group()
  private disposed = false

  private readonly withCredentials: boolean

  constructor(options?: { withCredentials?: boolean }) {
    this.withCredentials = options?.withCredentials ?? false
    this.debugRoot.name = 'spatialQueryDebugRoot'
    this.debugScene.add(this.debugRoot)
  }

  /**
   * 初始化插件，注册 SpatialQueryService。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.disposed = false
    this.service = new SpatialQueryServiceImpl({
      events: context.events,
      loadingManager: context.loadingManager,
      getViewport: () => {
        const rect = context.container.getBoundingClientRect()
        return {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        }
      },
      getViewRegistry: () => this.viewRegistry,
      getDebugRoot: () => this.debugRoot,
      resolveOwnerHandle: (kind, id) => this.resolveOwnerHandle(kind, id),
      getSharedOcclusionDepthService: () =>
        context.hasService('SharedOcclusionDepthService')
          ? context.getService('SharedOcclusionDepthService')
          : null,
      onSharedOcclusionDepthChanged: (reason) => {
        if (!context.hasService('RenderInvalidationService')) {
          return
        }
        context.getService('RenderInvalidationService').invalidate(reason)
      },
      withCredentials: this.withCredentials,
    })

    context.registerService('SpatialQueryService', this.service)
  }

  /** 启动插件，解析依赖并注册到 RenderPipeline */
  onStart(): void {
    if (!this.context) {
      return
    }

    this.renderPipeline = this.context.getService('RenderPipeline')
    this.renderPipeline.addRenderContributor(this)
    this.viewRegistry = this.context.getService('ViewRegistry')
    this.pointCloudService = this.context.hasService('PointCloudService')
      ? this.context.getService('PointCloudService')
      : null
    this.gaussianSplatService = this.context.hasService('GaussianSplatService')
      ? this.context.getService('GaussianSplatService')
      : null
    this.cadService = this.context.hasService('CadService')
      ? this.context.getService('CadService')
      : null
    this.tilesService = this.context.hasService('TilesService')
      ? this.context.getService('TilesService')
      : null
  }

  /** 每帧驱动服务更新 */
  onUpdateFrame(_frame: EngineFrameContext): void {
    this.service?.update()
  }

  /**
   * 渲染调试几何到指定视口。
   *
   * @param frame - 视图帧上下文，提供相机
   * @param _phase - 当前渲染阶段
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    if (!this.service || this.debugRoot.children.length === 0) {
      return
    }

    const hasVisibleDebugObject = this.debugRoot.children.some((child) => child.visible)
    if (!hasVisibleDebugObject) {
      return
    }

    // 调试几何与主场景分离，统一走受控 scene 提交；viewport/scissor 与 target 由 backend 依当前 View 决定。
    submitSceneInView(ctx, this.debugScene, frame.camera)
  }

  /** 销毁插件，从 RenderPipeline 注销并释放资源 */
  onDestroy(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true

    this.renderPipeline?.removeRenderContributor(this)
    this.service?.dispose()
    this.service = null
    this.renderPipeline = null
    this.pointCloudService = null
    this.gaussianSplatService = null
    this.cadService = null
    this.tilesService = null
    this.viewRegistry = null
    this.debugRoot.clear()
    this.context = null
  }

  private resolveOwnerHandle(kind: AssetKind, id: AssetId): AssetHandle | undefined {
    switch (kind) {
      case 'pointCloud':
        return this.pointCloudService?.getAsset(id)
      case 'gaussian-splat':
        return this.gaussianSplatService?.getAsset(id)
      case 'cad':
        return this.cadService?.getAsset(id)
      case 'tiles':
        return this.tilesService?.getAsset(id)
      default:
        return undefined
    }
  }
}
